import Microgame from '../engine/Microgame.js';

export default class CardGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Paires !`, en: `Pairs!`, es: `¡Parejas!` },
            controls: { fr: `Appuie sur les cartes pour les retourner`, en: `Tap cards to flip them`, es: `Toca las cartas para girarlas` },
            objective: { fr: `Trouve et associe toutes les paires identiques`, en: `Find all matching pairs`, es: `Encuentra todas las parejas idénticas` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><rect x="4" y="4" width="7" height="16" rx="1"/><rect x="13" y="4" width="7" height="16" rx="1"/></svg>`,
            actions: [
                { type: 'wait', duration: 260 },
                { type: 'swipe', fromX: 50, fromY: 50, toX: 14, toY: 14, duration: 520 },
                { type: 'swipe', fromX: 50, fromY: 50, toX: 86, toY: 14, duration: 520 },
                { type: 'swipe', fromX: 50, fromY: 50, toX: 86, toY: 86, duration: 520 },
                { type: 'swipe', fromX: 50, fromY: 50, toX: 14, toY: 86, duration: 520 },
                { type: 'wait', duration: 420 },
            ],
            loopDuration: 3600
        };
    }
    constructor(engine) {
        super(engine);
        this.cards = [];
        this.cardsDealt = 0;
        this.targetDeals = 4;
        this.topCard = null;
        this.startX = 0;
        this.startY = 0;

        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.35;
        this.duration = 5500 * (speedMult / adj);

        // Dealing order: either clockwise or counter-clockwise
        const cw = [0, 1, 3, 2];
        const ccw = [0, 2, 3, 1];
        this.isClockwise = this.isPlayback ? true : (Math.random() > 0.5);
        const sequence = this.isClockwise ? cw : ccw;
        const startIndex = this.isPlayback ? 0 : Math.floor(Math.random() * 4);
        
        this.dealOrder = [];
        for (let i = 0; i < 4; i++) {
            this.dealOrder.push(sequence[(startIndex + i) % 4]);
        }
        this.currentDealIndex = 0;

        // Corner definitions: top-left, top-right, bottom-left, bottom-right
        this.corners = [
            { label: '↖', x: 0, y: 0 },
            { label: '↗', x: 1, y: 0 },
            { label: '↙', x: 0, y: 1 },
            { label: '↘', x: 1, y: 1 },
        ];
    }

    shuffleArray(arr) {
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
    }

    getCardSVG(index) {
        const color = index % 2 === 0 ? this.primaryColor : this.secondaryColor;
        const svgs = [
            `<svg viewBox="0 0 24 24" width="48" height="48" fill="${color}"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01z"/></svg>`,
            `<svg viewBox="0 0 24 24" width="48" height="48" fill="${color}"><path d="M12 2L2 12l10 10 10-10z"/></svg>`,
            `<svg viewBox="0 0 24 24" width="48" height="48" fill="${color}"><path d="M7 2v11h3v9l7-12h-4l4-8z"/></svg>`,
            `<svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="${color}" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="5" fill="${color}"/></svg>`,
        ];
        return svgs[index % svgs.length];
    }

    setup() {
        this.engine.ui.instruction.textContent = "DEAL!";

        // Create receiving zones in each corner
        this.zoneElements = [];
        const zoneSize = 70;
        this.corners.forEach((corner, i) => {
            const zone = document.createElement('div');
            zone.style.position = 'absolute';
            zone.style.width = `${zoneSize}px`;
            zone.style.height = `${zoneSize}px`;
            zone.style.border = `2px dashed ${this.secondaryColor}55`;
            zone.style.borderRadius = '8px';
            zone.style.background = `${this.primaryColor}15`;

            if (corner.x === 0) zone.style.left = '8px';
            else zone.style.right = '8px';
            if (corner.y === 0) zone.style.top = '8px';
            else zone.style.bottom = '8px';

            zone.style.transition = 'border-color 0.3s, background 0.3s';
            zone.style.zIndex = '0';
            this.container.appendChild(zone);
            this.zoneElements.push(zone);
        });

        // Create cards
        for (let i = 0; i < this.targetDeals; i++) {
            this.createCard(i);
        }

        // Arrow indicator
        this.arrow = document.createElement('div');
        this.arrow.style.position = 'absolute';
        this.arrow.style.top = 'calc(50% - 130px)';
        this.arrow.style.left = '50%';
        this.arrow.style.transform = 'translateX(-50%)';
        this.arrow.style.fontSize = '48px';
        this.arrow.style.color = this.secondaryColor;
        this.arrow.style.textShadow = `0 0 10px ${this.secondaryColor}`;
        this.arrow.style.zIndex = '20';
        this.arrow.style.fontWeight = 'bold';
        this.arrow.textContent = this.isClockwise ? '↻' : '↺';
        this.container.appendChild(this.arrow);

        if (!document.getElementById('card-blink')) {
            const style = document.createElement('style');
            style.id = 'card-blink';
            style.innerHTML = `
                @keyframes cardZoneBlink {
                    0%, 100% { opacity: 0.8; box-shadow: 0 0 5px currentColor; transform: scale(1); }
                    50% { opacity: 1; box-shadow: 0 0 25px currentColor; transform: scale(1.1); }
                }
            `;
            document.head.appendChild(style);
        }

        this.highlightCurrentZone();

        // Input
        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    updateArrow() { /* No-op: Arrow is static */ }

    highlightCurrentZone() {
        // Dim all zones, remove animation
        this.zoneElements.forEach((z, i) => {
            z.style.borderColor = `${this.secondaryColor}33`;
            z.style.background = `${this.primaryColor}10`;
            z.style.animation = 'none';
        });

        // Only highlight and blink the FIRST deal corner
        if (this.currentDealIndex === 0) {
            const idx = this.dealOrder[0];
            this.zoneElements[idx].style.color = this.secondaryColor; // for box-shadow currentColor
            this.zoneElements[idx].style.borderColor = this.secondaryColor;
            this.zoneElements[idx].style.background = `${this.secondaryColor}25`;
            this.zoneElements[idx].style.animation = 'cardZoneBlink 0.6s infinite';
        }
    }

    createCard(index) {
        const card = document.createElement('div');
        card.style.position = 'absolute';
        card.style.width = '100px';
        card.style.height = '150px';
        card.style.background = index % 2 === 0 ? this.primaryColor : this.secondaryColor;
        card.style.border = `2px solid ${index % 2 === 0 ? this.secondaryColor : this.primaryColor}`;
        card.style.borderRadius = '10px';
        card.style.top = '50%';
        card.style.left = '50%';
        const rot = (Math.random() - 0.5) * 8;
        card.style.transform = `translate(-50%, -50%) rotate(${rot}deg)`;
        card.style.zIndex = 10 + index;
        card.style.boxShadow = '0 5px 15px rgba(0,0,0,0.5)';

        // SVG Symbol
        const symbolContainer = document.createElement('div');
        symbolContainer.innerHTML = this.getCardSVG(index);
        symbolContainer.style.position = 'absolute';
        symbolContainer.style.top = '50%';
        symbolContainer.style.left = '50%';
        symbolContainer.style.transform = 'translate(-50%, -50%)';
        symbolContainer.style.filter = `drop-shadow(0 0 6px ${index % 2 === 0 ? this.secondaryColor : this.primaryColor})`;
        card.appendChild(symbolContainer);

        // Corner symbols
        const cornerSVG = this.getCardSVG(index).replace('width="48" height="48"', 'width="14" height="14"');
        const topCorner = document.createElement('div');
        topCorner.innerHTML = cornerSVG;
        topCorner.style.position = 'absolute';
        topCorner.style.top = '6px';
        topCorner.style.left = '6px';
        card.appendChild(topCorner);

        const bottomCorner = document.createElement('div');
        bottomCorner.innerHTML = cornerSVG;
        bottomCorner.style.position = 'absolute';
        bottomCorner.style.bottom = '6px';
        bottomCorner.style.right = '6px';
        bottomCorner.style.transform = 'rotate(180deg)';
        card.appendChild(bottomCorner);

        this.container.appendChild(card);
        this.cards.push(card);

        if (index === this.targetDeals - 1) {
            this.topCard = card;
        }
    }

    onDown(e) {
        if (this.won || this.lost) return;
        this.startX = e.clientX;
        this.startY = e.clientY;
    }

    onMove(e) {
        if (!this.startX || !this.topCard) return;
        const dx = e.clientX - this.startX;
        const dy = e.clientY - this.startY;
        this.topCard.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${dx * 0.1}deg)`;
    }

    onUp(e) {
        if (!this.startX || !this.topCard) return;
        const dx = e.clientX - this.startX;
        const dy = e.clientY - this.startY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 80) {
            // Determine swipe direction → which corner
            const swipedCorner = this.getSwipedCorner(dx, dy);
            const targetCorner = this.dealOrder[this.currentDealIndex];

            if (swipedCorner === targetCorner) {
                this.dealCard(dx, dy, targetCorner);
            } else {
                // Wrong direction → lose!
                this.dealCardWrong(dx, dy);
            }
        } else {
            this.topCard.style.transform = `translate(-50%, -50%)`;
        }
        this.startX = 0;
        this.startY = 0;
    }

    getSwipedCorner(dx, dy) {
        // top-left=0, top-right=1, bottom-left=2, bottom-right=3
        if (dx <= 0 && dy <= 0) return 0;
        if (dx > 0 && dy <= 0) return 1;
        if (dx <= 0 && dy > 0) return 2;
        return 3;
    }

    dealCard(dx, dy, cornerIdx) {
        const card = this.cards.pop();
        const corner = this.corners[cornerIdx];

        // Ensure transition includes top and left
        card.style.transition = 'top 0.25s ease-out, left 0.25s ease-out, transform 0.25s ease-out, opacity 0.2s ease-out';
        
        // Exact center of the corner zones (70px zone + 8px margin = 43px to center)
        card.style.left = corner.x === 0 ? '43px' : 'calc(100% - 43px)';
        card.style.top = corner.y === 0 ? '43px' : 'calc(100% - 43px)';

        // Scale down and rotate smoothly when flying into the corner
        card.style.transform = `translate(-50%, -50%) rotate(${dx * 0.3}deg) scale(0.55)`;
        card.style.opacity = '0.9';
        card.style.zIndex = '1';

        // Mark zone as filled smoothly
        this.zoneElements[cornerIdx].style.borderColor = `${this.primaryColor}`;
        this.zoneElements[cornerIdx].style.background = `${this.primaryColor}35`;
        this.zoneElements[cornerIdx].style.animation = 'none';

        this.cardsDealt++;
        this.currentDealIndex++;

        if (this.cardsDealt >= this.targetDeals) {
            setTimeout(() => this.win(), 300);
        } else {
            this.topCard = this.cards[this.cards.length - 1];
            this.updateArrow();
            this.highlightCurrentZone();
        }
    }

    dealCardWrong(dx, dy) {
        const card = this.topCard;
        // Red flash on the card
        card.style.transition = 'transform 0.15s, box-shadow 0.15s';
        card.style.transform = `translate(calc(-50% + ${dx * 0.5}px), calc(-50% + ${dy * 0.5}px))`;
        card.style.boxShadow = '0 0 20px rgba(255,50,50,0.8)';
        
        setTimeout(() => {
            this.lose();
        }, 200);
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
